import { FIELD_MODES } from '../../components/fields/FieldTools';
import React, { useState } from 'react';
import PageHeader from '../../tenant_portal/shared/PageHeader';
import Text from '../../tenant_portal/fields/Text';
import PortalButton from '../../components/PortalButton';
import { useLoaderData, useParams } from 'react-router-dom';
import { graphql } from 'relay-runtime';
import {
  PreloadedQuery,
  useMutation,
  usePreloadedQuery,
  useQueryLoader,
} from 'react-relay';
import { RequestLinkLoginMutation$data } from './__generated__/RequestLinkLoginMutation.graphql';
import SubmitSpinner from '../../components/SubmitSpinner';
import SuccessPage from '../../tenant_portal/shared/SuccessPage';
import { ErrorType } from '../../components/Types';
import {
  filterErrorsByPath,
  formatErrorMessages,
  formatProviderName,
} from '../../components/Tools';
import layoutStyles from '../../../../stylesheets/components/layout.module.scss';
import type {
  RequestLinkQuery as RequestLinkQueryType,
  RequestLinkQuery$data as RequestLinkQueryResponse,
} from './__generated__/RequestLinkQuery.graphql';
import classNames from 'classnames';
import loginStyles from '../../../../stylesheets/components/login.module.scss';
import SomethingWentWrongAlert from '../SomethingWentWrongAlert';

export const RequestLinkQuery = graphql`
  query RequestLinkQuery($token: String!) {
    oauthLinkActivation(token: $token) {
      oauthProvider {
        email
        provider
      }
    }
  }
`;

export const LoginRequestLinkMutation = graphql`
  mutation RequestLinkLoginMutation($input: LoginRequestLinkInput!) {
    loginRequestLink(input: $input) {
      errors {
        path
        messages
      }
    }
  }
`;

const RequestLink = () => {
  const [emailField, setEmailField] = useState('');
  const [isEmailSent, setIsEmailSent] = useState(false);
  const { oauthLinkActivationToken } = useParams();
  const [commitMutation, isMutationInFlight] = useMutation(
    LoginRequestLinkMutation
  );
  const [errors, setErrors] = useState<ErrorType[]>([]);
  const baseErrors = filterErrorsByPath(errors);
  const formattedBaseErrorMessages = formatErrorMessages(baseErrors, '').trim();
  const somethingWentWrongAlert = (
    <SomethingWentWrongAlert
      body={formattedBaseErrorMessages}
      resetKey={errors}
    />
  );

  const handleSubmit = () => {
    commitMutation({
      variables: {
        input: {
          email: emailField,
          oauthLinkActivationToken: oauthLinkActivationToken,
        },
      },
      onCompleted: (response) => {
        const responseData = response as RequestLinkLoginMutation$data;
        console.log('completed! response: ', responseData);
        if (!responseData || !responseData.loginRequestLink) {
          setErrors([
            {
              path: 'base',
              messages: [
                'There was an error. Please contact Property Matrix support for assistance.',
              ],
            },
          ]);
          return;
        }
        if (
          responseData.loginRequestLink.errors &&
          responseData.loginRequestLink.errors.length > 0
        ) {
          console.log('error: ', responseData.loginRequestLink.errors);
          setErrors(responseData.loginRequestLink.errors as ErrorType[]);
          return;
        }
        setIsEmailSent(true);
      },
      onError: (error) => {
        setErrors([
          {
            path: 'base',
            messages: [
              'There was an error. Please contact Property Matrix support for assistance.',
            ],
          },
        ]);
        console.error(error);
      },
    });
  };

  const loaderQueryReference = useLoaderData() as PreloadedQuery<
    RequestLinkQueryType,
    RequestLinkQueryResponse
  >;

  const [queryReference] = useQueryLoader<RequestLinkQueryType>(
    RequestLinkQuery,
    loaderQueryReference
  );

  const loginActivationData = usePreloadedQuery<RequestLinkQueryType>(
    RequestLinkQuery,
    queryReference as PreloadedQuery<
      RequestLinkQueryType,
      RequestLinkQueryResponse
    >
  );

  const oauthProviderData =
    loginActivationData.oauthLinkActivation?.oauthProvider;

  if (!oauthProviderData) {
    return <div>There was an error.</div>;
  }

  const formattedProviderName = formatProviderName(oauthProviderData.provider);

  if (isMutationInFlight)
    return <SubmitSpinner spinnerText={'Sending email...'} />;

  if (isEmailSent)
    return (
      <SuccessPage
        successText="Email sent"
        successSubText="Please check your email for a link to link your accounts."
        variant="login-success"
      />
    );

  return (
    <div
      className={classNames(
        layoutStyles.fullWidth,
        loginStyles.loginRouteContent
      )}
    >
      {somethingWentWrongAlert}
      <PageHeader
        title={`Link Your ${formattedProviderName} Email`}
        includeBottomMargin={false}
      />
      <span
        className={classNames(
          layoutStyles.block,
          layoutStyles.mb20,
          loginStyles.loginParagraph
        )}
      >
        {oauthProviderData.email} wasn&apos;t found. Your{' '}
        {formattedProviderName} email may differ from the one on your account.
      </span>
      <span
        className={classNames(
          layoutStyles.block,
          layoutStyles.mb30,
          loginStyles.loginParagraph
        )}
      >
        Enter your Property Matrix email to enable {formattedProviderName}{' '}
        sign-in.
      </span>
      <div className={loginStyles.loginForm}>
        <Text
          field={{
            label: 'Email address',
            name: 'email',
            type: 'email',
            value: emailField,
            autocomplete: 'email',
          }}
          mode={FIELD_MODES.EDIT}
          onChange={(fieldName, value) =>
            setEmailField(value?.toString() || '')
          }
          errors={filterErrorsByPath(errors, ['email'])}
        />
      </div>
      <PortalButton
        onClick={handleSubmit}
        extraClasses={[
          loginStyles.loginPrimaryButton,
          loginStyles.linkAccountsButton,
        ]}
      >
        Link Accounts
      </PortalButton>
    </div>
  );
};

export default RequestLink;
